import { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import clsx from 'clsx'

import { Select } from '@wartek-id/select'
import { Text } from '@wartek-id/text-v1'
import { Checkbox, CheckboxGroup } from '@wartek-id/checkbox-v1'
import { Shimmer } from '@wartek-id/shimmer'

import { PelatihanMandiriList } from 'app/KinerjaGpkPage/components/PelatihanMandiriList'
import {
  SectionCard,
  SectionCardHeader,
  SectionCardBody,
} from 'app/KinerjaShared/components/SectionCard'
import DukunganTindakLanjutSelect from 'app/KinerjaShared/components/DukunganTindakLanjutSelect'
import { GPK_DUKUNGAN_UPAYA_TINDAK_LANJUT } from 'app/KinerjaGpkPage/constants/contents'
import { assetUrl } from 'utils/url'
import {
  useAPIPelaksanaanProgress,
  useAPIGetFollowUpTargets,
} from 'api/kinerja/useAPIKinerja'

import type { Path, FieldValues } from 'react-hook-form'
import type { MultiPeriodPayload } from 'types/kinerja'

type PilihanBelajarRgtkSectionFormProps<
  TFormValues extends FieldValues = FieldValues
> = {
  /**
   * Path to a field of type Record<string, string> where:
   * - Key: item.id (tujuan tindak lanjut ID)
   * - Value: learningModuleId (selected learning module ID)
   */
  tujuanFieldName: Path<TFormValues>
  dukunganFieldName: Path<TFormValues>
  periodParams: MultiPeriodPayload
  isLoading: boolean
  isReadOnly: boolean
}

export function PilihanBelajarRgtkSectionForm<
  TFormValues extends FieldValues = FieldValues
>({
  periodParams,
  isLoading: isLoadingProp,
  isReadOnly,
  tujuanFieldName,
  dukunganFieldName,
}: PilihanBelajarRgtkSectionFormProps<TFormValues>) {
  const formMethods = useFormContext<TFormValues>()

  if (!formMethods) {
    throw new Error(
      'PilihanBelajarRgtkSectionForm must be used within `<FormProvider />`'
    )
  }

  // -----------------------------------------
  // Resolve required status
  // -----------------------------------------
  const dukunganValue = formMethods.watch(dukunganFieldName)
  const tujuanTindakLanjutValue = formMethods.watch(tujuanFieldName)
  const isRequired = useMemo(() => {
    const hasDukungan = !!dukunganValue
    const hasTujuanTindakLanjut =
      tujuanTindakLanjutValue &&
      typeof tujuanTindakLanjutValue === 'object' &&
      Object.keys(tujuanTindakLanjutValue).length > 0

    if (isReadOnly) {
      return hasDukungan && hasTujuanTindakLanjut
    }

    return hasDukungan || hasTujuanTindakLanjut

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dukunganValue, tujuanTindakLanjutValue])

  // -----------------------------------------
  // Get Pelaksanaan Progress data
  // -----------------------------------------
  const {
    isLoading: isPelaksanaanProgressLoading,
    data: pelaksanaanProgressData,
  } = useAPIPelaksanaanProgress({
    tipe: 'GPK',
    periodParams: {
      skp_period: periodParams.skpPeriod,
      skp_year: periodParams.skpYear,
    },
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const contentId = pelaksanaanProgressData?.data?.contentId

  // -----------------------------------------
  // Get Tujuan Tindak Lanjut data
  // -----------------------------------------
  const {
    data: tujuanTindakLanjutData,
    isLoading: isTujuanTindakLanjutLoading,
  } = useAPIGetFollowUpTargets(
    { praktikPembelajaranId: contentId },
    {
      enabled: !!contentId,
      useErrorBoundary: true,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
    false
  )

  // -----------------------------------------
  // Other derived values
  // -----------------------------------------
  const isLoading =
    isPelaksanaanProgressLoading || isTujuanTindakLanjutLoading || isLoadingProp

  // Render nothing if the form is read-only and not required
  if (isReadOnly && !isRequired) {
    return null
  }

  return (
    <SectionCard className="mb-6">
      <SectionCardHeader
        variant="light-blue"
        className="flex items-center space-x-4"
      >
        <img
          src={assetUrl('/illustrations/img-smartphone-star.png')}
          width="64"
          height="64"
          alt=""
        />
        <Text as="h2" color="inherit" variant="heading-lg">
          Opsional: Pilihan Belajar dari Ruang GTK
        </Text>
      </SectionCardHeader>
      <SectionCardBody className="px-9 py-6 space-y-12">
        {isLoading ? (
          <div className="space-y-4">
            <Shimmer height={32} width="100%" />
            <Shimmer height={120} width="100%" />
            <Shimmer height={120} width="100%" />
            <Shimmer height={120} width="100%" />

            <Shimmer height={32} width="100%" />
            <Shimmer height={144} width="100%" />
          </div>
        ) : (
          <div className="space-y-4">
            <fieldset className="space-y-2">
              <Text variant="heading-sm">
                Apa tujuan tindak lanjut yang ingin Anda lakukan untuk
                meningkatkan kualitas Praktik Kinerja Anda?
              </Text>
              <div className="space-y-1 max-w-full">
                <Text as="label" color="subdued">
                  Pilih minimal 1 tujuan tindak lanjut (berdasarkan Perdirjen
                  GTK Nomor 2626 Tahun 2023 Tentang Model Kompetensi Guru)
                </Text>

                {isReadOnly ? (
                  <PelatihanMandiriList periodParams={periodParams} />
                ) : (
                  <Controller
                    control={formMethods.control}
                    name={tujuanFieldName}
                    rules={{
                      validate: {
                        minimumOneSelected: (fieldValue) => {
                          if (!isRequired) return true

                          // Ensure at least one Tujuan Tindak Lanjut is selected
                          return Object.values(fieldValue).length > 0
                        },
                        pelatihanMandiriSelected: (fieldValue) => {
                          if (!isRequired) return true

                          // Ensure Pelatihan Mandiri of each selected Tujuan Tindak Lanjut is selected
                          return Object.values(fieldValue).every(
                            (value) => value !== ''
                          )
                        },
                      },
                    }}
                    render={({
                      field: { onChange, value, name },
                      fieldState: { error },
                    }) => {
                      // value is Record<string, string> | undefined
                      // Convert Record keys to array for CheckboxGroup value prop
                      const checkedIds = value ? Object.keys(value) : []

                      const hasNoSelectedTujuan =
                        isRequired && error?.type === 'minimumOneSelected'
                      const hasUnselectedPelatihanMandiri =
                        isRequired && error?.type === 'pelatihanMandiriSelected'

                      return (
                        <div className="space-y-2">
                          <CheckboxGroup
                            name={name}
                            value={checkedIds}
                            onChange={(checkboxValue) => {
                              // checkboxValue is string[] of checked IDs
                              const newValue: Record<string, string> = {}
                              checkboxValue.forEach((id) => {
                                // Preserve existing learningModuleId if checkbox was already checked
                                newValue[id] = value?.[id] || ''
                              })
                              onChange(newValue)
                            }}
                            className="flex flex-row gap-4 flex-wrap"
                          >
                            {tujuanTindakLanjutData?.data?.map((item) => {
                              const isTujuanChecked = checkedIds.includes(
                                item.id
                              )
                              const selectedModuleId = value?.[item.id] || ''

                              const isPelatihanMandiriInvalid =
                                isTujuanChecked &&
                                hasUnselectedPelatihanMandiri &&
                                !selectedModuleId

                              return (
                                <Checkbox
                                  key={item.id}
                                  value={item.id}
                                  labelPosition="start"
                                  className={clsx(
                                    'p-3 border rounded w-full',
                                    hasNoSelectedTujuan
                                      ? 'border-red-500'
                                      : 'border-subdued'
                                  )}
                                >
                                  <div
                                    className="flex flex-col space-y-2 max-w-2xl"
                                    style={{ width: '60vw' }}
                                  >
                                    <Text
                                      as="span"
                                      variant="action"
                                      color={
                                        hasNoSelectedTujuan
                                          ? 'critical'
                                          : 'default'
                                      }
                                    >
                                      {item.subIndicator}
                                    </Text>
                                    <Text as="span">
                                      Pilih modul Pelatihan Mandiri
                                    </Text>

                                    <Select
                                      className="block overflow-wrap-anywhere whitespace-break-spaces"
                                      placeholder="Pilih di sini"
                                      value={selectedModuleId}
                                      onChange={(selectValue) => {
                                        const newValue = { ...(value || {}) }
                                        newValue[item.id] =
                                          selectValue.target.value
                                        onChange(newValue)
                                      }}
                                      isDisabled={
                                        isReadOnly || !isTujuanChecked
                                      }
                                      isInvalid={isPelatihanMandiriInvalid}
                                      errorMessage={
                                        isPelatihanMandiriInvalid
                                          ? 'Wajib dipilih'
                                          : ''
                                      }
                                    >
                                      <option
                                        disabled
                                        value=""
                                        className="text-disabled"
                                      >
                                        Pilih di sini
                                      </option>
                                      {item?.learningModules?.map(
                                        (module, i) => (
                                          <option key={i} value={module.id}>
                                            {module.title}
                                          </option>
                                        )
                                      )}
                                    </Select>
                                  </div>
                                </Checkbox>
                              )
                            })}
                          </CheckboxGroup>

                          {hasNoSelectedTujuan && (
                            <Text variant="helper" color="critical">
                              Minimal 1 tujuan tindak lanjut dipilih
                            </Text>
                          )}
                        </div>
                      )
                    }}
                  />
                )}
              </div>
            </fieldset>

            <DukunganTindakLanjutSelect<TFormValues>
              options={GPK_DUKUNGAN_UPAYA_TINDAK_LANJUT}
              isReadOnly={isReadOnly}
              isRequired={isRequired}
              fieldName={dukunganFieldName}
            />
          </div>
        )}
      </SectionCardBody>
    </SectionCard>
  )
}
